// apps/spa/src/hooks/useAuditLog.ts
// TanStack Query (v5) infinite-query hook for /api/audit-log cursor pagination.
//
// API contract: GET /api/audit-log?cursor=<ts>&limit=50
//   - No cursor = first page (50 newest rows).
//   - cursor=<ts> = next page older than ts.
//   - Returns { entries: EnrichedAuditLogEntry[], nextCursor: number | null }
//   - nextCursor = ts of oldest row in page; null when fewer than 50 rows returned (end of data).
//
// Usage in AuditLog.tsx:
//   const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useAuditLog();
//   const entries = data?.pages.flatMap(p => p.entries) ?? [];

import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { EnrichedAuditLogEntry } from '@guardian/shared';

export type AuditLogPage = {
  entries: EnrichedAuditLogEntry[];
  nextCursor: number | null;
};

export function useAuditLog() {
  return useInfiniteQuery({
    queryKey: ['audit-log'] as const,
    queryFn: ({ pageParam }) => {
      const path =
        pageParam !== null
          ? `/api/audit-log?cursor=${pageParam}`
          : '/api/audit-log';
      return api<AuditLogPage>(path);
    },
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}
